import { memo, forwardRef, useCallback, useMemo, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  type MenuProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { bloxValues } from 'themes/constants';
import { DragModal, DragModalHeader } from 'components/DragModal/DragModal';
import { Button, Box } from '@mui/material';
import type { TDashboard } from '../../utils/constants';
import { TICKER_DASHBOARDS } from 'screens/dashboard/constants';
import useDashboardPermissions from 'screens/dashboard/hooks/useDashboardPermissions';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface DashboardMiniMenuConfig {
  edit?: { enabled?: boolean };
  duplicate?: { enabled?: boolean };
  delete?: { enabled?: boolean };
}

export const allDashboardMenuOptions: DashboardMiniMenuConfig = {
  edit: { enabled: true },
  duplicate: { enabled: true },
  delete: { enabled: true },
};

interface BaseMiniMenuProps {
  menuEvent: React.MouseEvent | null;
  handleMiniMenuClose: () => void;
  clearSelectedItems?: () => void;
  anchorEl?: HTMLElement | null;
}

export interface DashboardMiniMenuProps extends BaseMiniMenuProps {
  selectedItems: TDashboard[];
  menuConfig?: DashboardMiniMenuConfig;
  contextMenuItem?: TDashboard;
  isContextItemDisabled?: boolean;
  onEdit?: (dashboard: TDashboard) => void;
  onDuplicate?: (dashboard: TDashboard) => void;
  onDelete?: (dashboards: TDashboard[]) => void;
}

type ButtonState = {
  show: boolean;
  disabled: boolean;
  info?: string;
};

export const getIsTickerDashboard = (dashboard?: TDashboard): boolean => {
  const tickerDashboards = Object.values(TICKER_DASHBOARDS);
  return Boolean(tickerDashboards.includes(dashboard?.id ?? ''));
};

export const getIsDuplicateDisabled = ({
  selectedItems,
  contextMenuItem,
  isContextItemDisabled,
  isContextItemSelected,
}: {
  selectedItems: TDashboard[];
  contextMenuItem?: TDashboard;
  isContextItemDisabled?: boolean;
  isContextItemSelected?: boolean;
}): boolean => {
  const isTickerDashboard = getIsTickerDashboard(contextMenuItem);
  const hasMultipleSelected = selectedItems?.length > 1;
  return (
    Boolean(isContextItemDisabled && !isContextItemSelected) ||
    isTickerDashboard ||
    hasMultipleSelected
  );
};

// FIXME: why are some of the dashboards not rendering?
// FIXME:

export const DashboardMiniMenu = memo(
  forwardRef<HTMLDivElement, DashboardMiniMenuProps>(
    (
      {
        menuEvent,
        selectedItems,
        handleMiniMenuClose,
        clearSelectedItems,
        menuConfig = allDashboardMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        onEdit,
        onDuplicate,
        onDelete,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const {
        create: canCreate,
        edit: canEdit,
        delete: canDelete,
        share: canShare,
        full: fullAccess,
      } = useDashboardPermissions();

      const hasPermissionToEdit = canEdit || fullAccess;
      const hasPermissionToDuplicate = canCreate || fullAccess;
      const hasPermissionToDelete = canDelete || fullAccess;
      const hasPermissionToShare = canShare || fullAccess;

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Menu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // Determine which items to use for menu operations
      const hasSelections = Boolean(selectedItems?.length);
      const contextItemId = contextMenuItem?.id;
      const isContextItemSelected =
        hasSelections && contextItemId
          ? selectedItems.some((item) => item?.id === contextItemId)
          : false;

      const isDuplicateDisabled = getIsDuplicateDisabled({
        selectedItems,
        contextMenuItem,
        isContextItemDisabled,
        isContextItemSelected,
      });

      // Right click behaviors:
      // 1. If items are selected AND the right-clicked item is one of them -> use all selected items (multi-item mode)
      // 2. If items are selected BUT the right-clicked item is NOT selected -> use only the right-clicked item (single-item mode)
      // 3. If no items are selected -> use the right-clicked item (single-item mode)
      const effectiveItems = useMemo(
        () =>
          hasSelections && isContextItemSelected
            ? selectedItems
            : contextMenuItem
              ? [contextMenuItem]
              : [],
        [hasSelections, isContextItemSelected, selectedItems, contextMenuItem],
      );

      const selectedCount = effectiveItems?.length ?? 0;
      const hasMultipleSelected = Boolean(effectiveItems?.length > 1);
      const lastItem =
        effectiveItems?.length > 0
          ? effectiveItems[effectiveItems.length - 1]
          : undefined;

      // --------------------------------------------------
      // Styles
      // --------------------------------------------------
      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: theme.palette.text.secondary,
          mr: 1,
        };
      }, [theme]);

      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      // --------------------------------------------------
      // Edit Button
      // --------------------------------------------------
      const editState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        const canEdit = Boolean(lastItem?.id && !hasMultipleSelected);

        if (!canEdit) {
          return {
            disabled: true,
            show: showEdit,
          };
        }

        // Prevent editing of system dashboards
        if (lastItem?.type === 'system') {
          return {
            disabled: true,
            show: showEdit,
            info: t(
              'blox.dashboards.custom_dashboards.edit.system_dashboard_disabled',
              'This board cannot be edited.',
            ),
          };
        }

        return {
          disabled: false,
          show: showEdit,
        };
      }, [hasMultipleSelected, lastItem, menuConfig?.edit?.enabled, t]);

      const handleEdit = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          if (editState?.disabled || !lastItem) return;
          onEdit?.(lastItem);
          internalOnClose();
        },
        [editState, lastItem, onEdit, internalOnClose],
      );

      // --------------------------------------------------
      // Duplicate Button
      // --------------------------------------------------
      const duplicateState: ButtonState = useMemo(() => {
        const showDuplicate = Boolean(menuConfig?.duplicate?.enabled);
        const canDuplicate = Boolean(lastItem?.id && !isDuplicateDisabled);

        if (!canDuplicate) {
          return {
            disabled: true,
            show: showDuplicate,
          };
        }

        return {
          disabled: false,
          show: showDuplicate,
        };
      }, [isDuplicateDisabled, lastItem?.id, menuConfig?.duplicate?.enabled]);

      const handleDuplicate = useCallback(
        // TODO: need to modify the payload for the backend.
        // TODO: create normalization functions for the payload transformations needed for edit vs duplicate operations, since they will likely differ in some ways (e.g. duplicate may require a new name, whereas edit may not)
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          if (duplicateState?.disabled || !lastItem) {
            return;
          }
          onDuplicate?.(lastItem);
          internalOnClose();
        },
        [duplicateState, lastItem, onDuplicate, internalOnClose],
      );

      // --------------------------------------------------
      // Delete Button
      // --------------------------------------------------
      const deleteState: ButtonState = useMemo(() => {
        const showDelete = Boolean(menuConfig?.delete?.enabled);

        // Prevent deletion of current dashboard being viewed in modal
        if (isContextItemDisabled && !isContextItemSelected) {
          return {
            disabled: true,
            show: showDelete,
          };
        }

        const canDelete = Boolean(effectiveItems.length > 0);

        if (!canDelete) {
          return {
            disabled: true,
            show: showDelete,
          };
        }

        // Prevent deletion of system dashboards
        const hasSystemDashboard = effectiveItems.some((item) => item?.type === 'system');

        if (hasSystemDashboard) {
          return {
            disabled: true,
            show: showDelete,
            info: t(
              'blox.dashboards.custom_dashboards.delete.system_dashboard_disabled',
              'This board cannot be deleted.',
            ),
          };
        }

        return {
          disabled: false,
          show: showDelete,
        };
      }, [
        effectiveItems,
        menuConfig?.delete?.enabled,
        isContextItemDisabled,
        isContextItemSelected,
        t,
      ]);

      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

      const openDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(false);
      }, []);

      const handleDeleteConfirm = useCallback(async () => {
        if (!effectiveItems?.length) {
          console.error('[DashboardMiniMenu] No dashboards to delete.');
          internalOnClose();
          return;
        }

        onDelete?.(effectiveItems);
        clearSelectedItems?.();
        closeDeleteDialog();
        internalOnClose();
      }, [
        effectiveItems,
        onDelete,
        clearSelectedItems,
        closeDeleteDialog,
        internalOnClose,
      ]);

      const handleDeleteCancel = useCallback(() => {
        closeDeleteDialog();
      }, [closeDeleteDialog]);

      const handleDeleteMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (deleteState?.disabled) return;
          openDeleteDialog();
        },
        [deleteState?.disabled, openDeleteDialog],
      );

      const confirmDeleteCountStr = `${selectedCount} ${
        selectedCount === 1 ? 'dashboard' : 'dashboards'
      } will be deleted.`;

      return (
        <Fragment>
          <DragModal
            id="dashboard-delete-confirm-modal"
            open={confirmDeleteOpen}
            onClose={closeDeleteDialog}
            isDraggable={true}
            isResizable={false}
            isFullscreenable={false}
            initialWidth={400}
            initialHeight={220}
            header={
              <DragModalHeader
                text={t('blox.actions.confirm_delete', 'Confirm Delete')}
              />
            }
            body={<Box sx={{ p: 2 }}>{confirmDeleteCountStr}</Box>}
            footer={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleDeleteCancel}
                >
                  {t('blox.actions.cancel', 'Cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteConfirm}
                >
                  {t('blox.actions.delete', 'Delete')}
                </Button>
              </Box>
            }
          />

          <Menu
            ref={ref}
            open={isOpen}
            onClose={internalOnClose}
            sx={menuSx}
            slotProps={{
              paper: {
                elevation: 3,
              },
            }}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                })}
          >
            {/* Edit */}
            {editState.show && (
              <Tooltip
                title={editState.info || ''}
                placement="right"
                arrow
              >
                <span>
                  <MenuItem
                    onClick={handleEdit}
                    disabled={editState.disabled || !hasPermissionToEdit}
                  >
                    <ListItemIcon>
                      <EditIcon sx={iconSx} />
                    </ListItemIcon>
                    <ListItemText>{t('blox.actions.edit', 'Edit')}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            )}

            {/* Duplicate */}
            {duplicateState.show && (
              <MenuItem
                onClick={handleDuplicate}
                disabled={duplicateState.disabled || !hasPermissionToDuplicate}
              >
                <ListItemIcon>
                  <ContentCopyIcon sx={iconSx} />
                </ListItemIcon>
                <ListItemText>{t('blox.actions.duplicate', 'Duplicate')}</ListItemText>
              </MenuItem>
            )}

            {/* Delete */}
            {deleteState.show && (
              <Tooltip
                title={deleteState.info || ''}
                placement="right"
                arrow
              >
                <span>
                  <MenuItem
                    onClick={handleDeleteMenuClick}
                    disabled={deleteState.disabled || !hasPermissionToDelete}
                  >
                    <ListItemIcon>
                      <DeleteIcon sx={iconSx} />
                    </ListItemIcon>
                    <ListItemText>{t('blox.actions.delete', 'Delete')}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            )}
          </Menu>
        </Fragment>
      );
    },
  ),
);

DashboardMiniMenu.displayName = 'DashboardMiniMenu';
